"use client";
import React, { useEffect, useState } from "react";
import { Button } from "@nextui-org/react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import DefaultLayout from "@/layouts/default";
import { booksAPI, checkAuthentication } from "@/services/api"; // Import the books API service and checkAuthentication
import { formatDate } from "@/utils/dateUtils";

interface Book {
  id: string;
  name: string;
  author: string;
  category: string;
  publisher: string;
  stock: number;
  price: number;
  min_stocks?: number;
  price_per_book?: number;
  description?: string;
  language?: string;
  pageSize?: string;
  pageType?: string;
  pages?: string;
  publicationDate?: string;
}

const ViewBook: React.FC = () => {
  const [book, setBook] = useState<Book | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchBook = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError(null);

        // Check authentication status
        const authStatus = await checkAuthentication();

        if (!authStatus.authenticated) {
          setError("Authentication required. Please log in again.");
          setLoading(false);

          return;
        }

        const bookData = await booksAPI.getById(id);

        // Check if we have valid data
        if (!bookData || typeof bookData !== "object") {
          setError("Invalid data received from the server");
          setLoading(false);

          return;
        }

        // Format the data for consistency
        // Convert price fields to numbers - each field shows independently
        const pricePerBook = Number(bookData.price_per_book ?? bookData.price_perBook ?? 0) || 0;
        const price = Number(bookData.price ?? 0) || 0;
        
        const formattedBook = {
          ...bookData,
          // Ensure these fields exist with fallbacks and are numbers
          stock: Number(bookData.stock) || 0,
          price: price,
          min_stocks: Number(bookData.min_stocks ?? bookData.minStocks ?? 0) || 0,
          price_per_book: pricePerBook,
          // Handle different possible field formats
          description: bookData.description || "",
          language: bookData.language || "",
          pageSize: bookData.pageSize || bookData.page_size || "",
          pageType: bookData.pageType || bookData.page_type || "",
          pages: bookData.pages || "",
          publicationDate:
            bookData.publicationDate || bookData.publication_date || "",
          publisher: bookData.publisher || "",
        };

        setBook(formattedBook);
        setLoading(false);
      } catch (error: any) {
        setError(`Failed to fetch book: ${error.message || "Unknown error"}`);
        setLoading(false);
      }
    };

    fetchBook();
  }, [id]);

  const handleDelete = async () => {
    if (!id) return;
    try {
      const confirmDelete = window.confirm(
        t("Are you sure you want to delete this book?"),
      );

      if (!confirmDelete) return;

      setLoading(true);
      await booksAPI.delete(id);
      alert(t("Book deleted successfully!"));
      setLoading(false);
      navigate("/bookList");
    } catch (error: any) {
      setLoading(false);
      
      // Show more detailed error message
      const errorMessage = error.response?.data?.error || error.message || "Unknown error";
      alert(t("Failed to delete book: ") + errorMessage);
    }
  };

  if (loading) {
    return (
      <DefaultLayout>
        <div className="container mx-auto p-4">
          <p className="text-center">{t("Loading...")}</p>
        </div>
      </DefaultLayout>
    );
  }

  if (error) {
    return (
      <DefaultLayout>
        <div className="container mx-auto p-4">
          <div className="bg-red-500 text-white p-4 rounded mb-4">{error}</div>
          <Button color="primary" onClick={() => navigate("/bookList")}>
            {t("Back to Books")}
          </Button>
        </div>
      </DefaultLayout>
    );
  }

  if (!book) {
    return (
      <DefaultLayout>
        <div className="container mx-auto p-4">
          <p className="text-center">{t("Book not found")}</p>
          <Button
            className="mt-4"
            color="primary"
            onClick={() => navigate("/bookList")}
          >
            {t("Back to Books")}
          </Button>
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <section className="flex flex-col items-center justify-center py-8">
        <div className="max-w-lg w-full p-6 rounded-lg shadow-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <h2 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-4">
            {book.name}
          </h2>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {t("Author")}:
              </span>
              <span className="text-gray-900 dark:text-gray-200">
                {book.author}
              </span>
            </div>
            <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {t("Category")}:
              </span>
              <span className="text-gray-900 dark:text-gray-200">
                {book.category}
              </span>
            </div>
            <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {t("Language")}:
              </span>
              <span className="text-gray-900 dark:text-gray-200">
                {book.language || ""}
              </span>
            </div>
            <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {t("Pages")}:
              </span>
              <span className="text-gray-900 dark:text-gray-200">
                {book.pages || ""}
              </span>
            </div>
            <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {t("Publisher")}:
              </span>
              <span className="text-gray-900 dark:text-gray-200">
                {book.publisher}
              </span>
            </div>
            <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {t("Publication Date")}:
              </span>
              <span className="text-gray-900 dark:text-gray-200">
                {book.publicationDate ? formatDate(book.publicationDate) : ""}
              </span>
            </div>
            <div className="flex flex-col col-span-2 border-b-2 border-gray-300 dark:border-gray-700 p-2">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {t("Description")}:
              </span>
              <span className="text-gray-900 dark:text-gray-200">
                {book.description || ""}
              </span>
            </div>
            <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {t("Stock")}:
              </span>
              <span className="text-gray-900 dark:text-gray-200">
                {book.stock}
              </span>
            </div>
            <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {t("Minimum Stock")}:
              </span>
              <span className="text-gray-900 dark:text-gray-200">
                {book.min_stocks || 0}
              </span>
            </div>
            <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {t("Page Type")}:
              </span>
              <span className="text-gray-900 dark:text-gray-200">
                {book.pageType || ""}
              </span>
            </div>
            <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {t("Price Per Book")}:
              </span>
              <span className="text-gray-900 dark:text-gray-200">
                {book.price_per_book ? `${Number(book.price_per_book).toFixed(2)} Rs` : "0.00 Rs"}
              </span>
            </div>
            <div className="flex flex-col col-span-2 border-b-2 border-gray-300 dark:border-gray-700 p-4">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {t("Price")}:
              </span>
              <div className="flex items-center justify-center w-full mt-2 p-4 border-2 border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800">
                <span className="text-gray-900 dark:text-gray-200 text-2xl">
                  {book.price ? `${Number(book.price).toFixed(2)} Rs` : "0.00 Rs"}
                </span>
              </div>
            </div>
          </div>
          <div className="flex justify-center gap-4 mt-6">
            <Button color="primary" onClick={() => navigate(`/bookList`)}>
              {t("Back")}
            </Button>
            <Button color="primary" onClick={() => navigate(`/bookEdit/${id}`)}>
              {t("Edit Book")}
            </Button>
            <Button color="danger" onClick={handleDelete}>
              {t("Delete Book")}
            </Button>
          </div>
        </div>
      </section>
    </DefaultLayout>
  );
};

export default ViewBook;
